import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import TextField from './TextField';
import { useTranslation } from 'react-i18next';

/**
 * Компонент для редактирования контента лабиринта с буферизацией изменений
 * @param {string} content - JSON строка с содержимым лабиринта
 * @param {function} onChange - Функция обработки изменений
 */
const LabyrinthContentEditor = ({ content, onChange }) => {
  const { t } = useTranslation(['lessons', 'common']);
  // Локальное состояние для буферизации изменений
  const [localContent, setLocalContent] = useState(content);
  // Отслеживаем, изменился ли контент с момента последнего сохранения
  const [isDirty, setIsDirty] = useState(false);
  // Состояние для отображения ошибок валидации JSON
  const [jsonError, setJsonError] = useState(null);
  // Таймер для отложенного сохранения
  const saveTimeoutRef = useRef(null);

    // Валидация JSON
  const validateJson = useCallback((jsonString) => {
    try {
      if (jsonString.trim() === '') {
        setJsonError(null);
        return;
      }

      const parsed = JSON.parse(jsonString);

      // Базовая проверка структуры лабиринта
      if (!parsed.startNode || !parsed.nodes || !Array.isArray(parsed.nodes)) {
        setJsonError(t('lessons:editor.labyrinth.invalidStructure'));
        return;
      }

      setJsonError(null);
    } catch (error) {
      setJsonError(`${t('lessons:editor.labyrinth.jsonError')}: ${error.message}`);
    }
  }, [t]);

  // Синхронизируем локальное состояние с пропсами при их изменении
  useEffect(() => {
    setLocalContent(content);
    setIsDirty(false);
    validateJson(content);
  }, [content, validateJson]);

    // Непосредственное сохранение в родительский компонент
  const saveChangesToParent = useCallback(() => {
    if (isDirty && !jsonError) {
      onChange(localContent);
      setIsDirty(false);
    }
  }, [localContent, isDirty, jsonError, onChange]);

  // Эффект для отложенного сохранения с debounce
  useEffect(() => {
    if (isDirty) {
      // Очищаем предыдущий таймер
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Устанавливаем новый таймер для сохранения через 1 секунду
      saveTimeoutRef.current = setTimeout(() => {
        if (!jsonError) {
          saveChangesToParent();
        }
      }, 1000);
    }

    // Очистка при размонтировании
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [localContent, isDirty, jsonError, saveChangesToParent]);

  // Обработчик изменения контента
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setLocalContent(newContent);
    setIsDirty(true);
    validateJson(newContent);
  };

  // Обработчик потери фокуса для полей ввода
  const handleBlur = () => {
    // Сохраняем изменения при потере фокуса, если нет ошибок
    if (!jsonError) {
      saveChangesToParent();
    }
  };

  return (
    <Box>
      {jsonError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {jsonError}
        </Alert>
      )}

      <TextField
        fullWidth
        multiline
        rows={12}
        label={t('lessons:editor.labyrinth.contentLabel')}
        value={localContent}
        onChange={handleContentChange}
        onBlur={handleBlur}
        error={!!jsonError}
        sx={{ mt: 2 }}
        placeholder={t('lessons:editor.labyrinth.contentPlaceholder')}
      />

      <Box sx={{ mt: 2, fontSize: '0.85rem', color: 'text.secondary' }}>
        <Typography variant="caption" component="div" sx={{ display: 'block' }}>
          {t('lessons:editor.labyrinth.formatHelp')}:
        </Typography>
        <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
          <li>{t('lessons:editor.labyrinth.titleField', { fieldName: 'title' })}</li>
          <li>
            {t('lessons:editor.labyrinth.startNodeField', { fieldName: 'startNode' })}
          </li>
          <li>{t('lessons:editor.labyrinth.nodesField', { fieldName: 'nodes' })}</li>
        </ul>
      </Box>
    </Box>
  );
};

export default LabyrinthContentEditor;
